"use client";
import React, { useState, useEffect } from "react";
import PersonalChatView from "./PersonalChatView";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function PersonalView() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const headers = buildHeaders(token);

  useEffect(() => {
    if (!token) {
      setError("No token found — please sign in again.");
      return;
    }
    if (selectedChat) return;

    const fetchConversations = async () => {
      try {
        setLoading(true);
        
        const res = await fetch(`${API_URL}/messaging/conversations/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          setError("Unauthorized — please sign in again.");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setConversations(data.results || data);
      } catch (err) {
        console.error("❌ Fetch error:", err);
        setError("Failed to load conversations.");
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [token, selectedChat]);

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const userTimeZone = getUserTimeZone();
      const now = new Date();
      const messageDate = new Date(timestamp);
      const diffMs = now - messageDate;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      if (diffHours < 24) {
        return messageDate.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: userTimeZone,
        });
      } else if (diffHours < 48) {
        return "Yesterday";
      } else if (diffHours < 168) {
        return messageDate.toLocaleDateString([], {
          weekday: 'short',
          timeZone: userTimeZone,
        });
      } else {
        return messageDate.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          timeZone: userTimeZone,
        });
      }
    } catch {
      return "";
    }
  };

  if (selectedChat) {
    return (
      <div className="flex flex-row h-full w-full">
        <div className="flex-1 h-full">
          <PersonalChatView chat={selectedChat} onBack={() => setSelectedChat(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row h-full w-full">
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
        <p className="text-[22px]">Click on someone to start a conversation</p>
      </div>
    </div>
  );
}